/**
 * Driver Profile page.
 */
import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Trophy, Flag, Star, Activity } from "lucide-react";
import { api, useFetch } from "../lib/api";
import { SectionHeader, Reveal, EmptyState } from "../components/atoms";

export default function DriverProfile() {
    const { id } = useParams();
    const { data, loading } = useFetch(() => api.driverById(id), [id]);
    const { data: newsResp } = useFetch(() => api.news(20), [id]);

    const driver = data?.driver;

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="h-72 rounded-2xl shimmer" />
            </div>
        );
    }

    if (!driver) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <EmptyState title="Driver not found" message="This driver doesn't exist in our records." icon={Flag} />
            </div>
        );
    }

    const driverNews = (newsResp?.news || []).filter((n) =>
        n.related_driver_ids?.includes(driver.id)
    );

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="driver-profile-page">
            <Link to="/drivers" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
                <ArrowLeft className="h-4 w-4" /> Back to drivers
            </Link>

            {/* HERO */}
            <div className="relative mt-6 rounded-2xl overflow-hidden border border-white/10">
                <div
                    className="absolute inset-0"
                    style={{
                        background: `linear-gradient(135deg, ${driver.team_color}33, transparent 60%), radial-gradient(ellipse at top right, ${driver.team_color}22, transparent 70%)`,
                    }}
                />
                <div className="absolute inset-0 grid-bg opacity-30" />
                <div
                    className="absolute right-0 top-1/2 -translate-y-1/2 font-display text-[24rem] leading-none text-white/[0.04] select-none pointer-events-none"
                    aria-hidden
                >
                    {driver.number || ""}
                </div>
                <div className="relative p-8 sm:p-12 grid lg:grid-cols-2 gap-10 items-end min-h-[420px]">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <span className="label-eyebrow text-zinc-300">{driver.country?.toUpperCase()}</span>
                            <span className="h-3 w-px bg-white/20" />
                            <span className="label-eyebrow" style={{ color: driver.team_color }}>{driver.team_name?.toUpperCase()}</span>
                        </div>
                        <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl tracking-tight leading-[0.9]">
                            {driver.first_name}
                            <br />
                            <span style={{ color: driver.team_color }}>{driver.last_name}</span>
                        </h1>
                        <p className="mt-6 text-zinc-300 max-w-lg leading-relaxed">{driver.bio}</p>
                        {(driver.championships || 0) > 0 && (
                            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-400/10 border border-amber-400/30 text-amber-300">
                                <Trophy className="h-3.5 w-3.5" />
                                <span className="font-mono text-xs tracking-widest">
                                    {driver.championships}× WORLD CHAMPION
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* STATS GRID */}
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatBox label="Career Wins" value={driver.career_wins} />
                <StatBox label="Podiums" value={driver.career_podiums} />
                <StatBox label="Poles" value={driver.career_poles} />
                <StatBox label="Titles" value={driver.championships || 0} accent />
                {driver.status === "active" && (
                    <>
                        <StatBox label="Season Points" value={driver.points} />
                        <StatBox label="Season Wins" value={driver.wins} />
                        <StatBox label="Standing" value={`P${driver.standing}`} accent />
                        <StatBox label="Debut" value={driver.debut_year} />
                    </>
                )}
            </div>

            {/* RECENT FORM + STRENGTHS */}
            {driver.status === "active" && (
                <div className="mt-8 grid lg:grid-cols-3 gap-5">
                    <div className="lg:col-span-2 p-6 rounded-2xl glass-strong border border-white/5">
                        <div className="label-eyebrow mb-4">RECENT FORM · LAST 5 RACES</div>
                        <div className="flex items-end gap-3">
                            {(driver.recent_form || []).map((pos, i) => {
                                const h = Math.max(10, 100 - (pos - 1) * 8);
                                return (
                                    <div key={i} className="flex-1 flex flex-col items-center gap-2">
                                        <div className="font-mono text-[10px] text-zinc-500">P{pos}</div>
                                        <div
                                            className="w-full rounded-t-md"
                                            style={{
                                                height: `${h}px`,
                                                background: `linear-gradient(180deg, ${driver.team_color}, ${driver.team_color}33)`,
                                            }}
                                        />
                                        <div className="font-mono text-[9px] text-zinc-600">R-{5 - i}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className="p-6 rounded-2xl glass-strong border border-white/5">
                        <div className="label-eyebrow mb-4">STRENGTHS</div>
                        <ul className="space-y-2.5">
                            {(driver.strengths || []).map((s) => (
                                <li key={s} className="flex items-center gap-3 text-sm">
                                    <Star className="h-3.5 w-3.5 text-f1-red" />
                                    <span>{s}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {/* RELATED NEWS */}
            {driverNews.length > 0 && (
                <div className="mt-12">
                    <SectionHeader eyebrow="RELATED NEWS" title="Latest mentions" />
                    <div className="mt-6 grid sm:grid-cols-2 gap-4">
                        {driverNews.slice(0, 4).map((n) => (
                            <div key={n.id} className="p-5 rounded-xl bg-graphite-800/60 border border-white/5">
                                <div className="text-[10px] font-mono tracking-widest text-f1-red">{n.tag?.toUpperCase()}</div>
                                <h3 className="mt-1 font-display text-xl">{n.title}</h3>
                                <p className="mt-2 text-sm text-zinc-400">{n.summary}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function StatBox({ label, value, accent }) {
    return (
        <div className={`p-5 rounded-xl bg-graphite-800/60 border ${accent ? "border-f1-red/40" : "border-white/5"}`}>
            <div className="label-eyebrow">{label}</div>
            <div className={`font-display text-3xl mt-1 tabular-nums tracking-tight ${accent ? "text-f1-red" : ""}`}>
                {value ?? "—"}
            </div>
        </div>
    );
}
